import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader } from "@/components/PageHeader";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { useEffect } from "react";
import api from "@/lib/api";

export default function Settings() {
  const { t, lang, setLang } = useI18n();
  const { user, refreshUser } = useAuth();
  const [profile, setProfile] = useState({ full_name: "", professional_title: "", bio: "", phone: "" });
  const [countries, setCountries] = useState([]);
  const [pwd, setPwd] = useState({ current_password: "", new_password: "" });
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("profile");

  useEffect(() => {
    if (user?.profile) setProfile({ full_name: user.profile.full_name || "", professional_title: user.profile.professional_title || "", bio: user.profile.bio || "", phone: user.profile.phone || "" });
    api.get("/countries").then(({ data }) => setCountries(data)).catch(() => {});
  }, [user]);

  const saveProfile = async () => { setLoading(true); try { await api.put("/users/me/profile", profile); await refreshUser(); toast.success(t("settings.profileUpdated")); } catch (e) { toast.error(t("common.error")); } finally { setLoading(false); } };
  const saveSettings = async (patch) => { try { await api.put("/users/me/settings", patch); await refreshUser(); toast.success(t("common.success")); } catch (e) { toast.error(t("common.error")); } };
  const changePwd = async () => { if (pwd.new_password.length < 8) { toast.error("Min. 8 caractères"); return; } try { await api.post("/users/me/change-password", pwd); setPwd({ current_password: "", new_password: "" }); toast.success(t("common.success")); } catch (e) { toast.error(e.response?.data?.detail || t("common.error")); } };

  return (
    <div className="max-w-3xl mx-auto">
      <PageHeader title={t("settings.title")} />
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="profile" data-testid="tab-profile">{t("settings.profile")}</TabsTrigger>
          <TabsTrigger value="preferences" data-testid="tab-preferences">{t("settings.preferences")}</TabsTrigger>
          <TabsTrigger value="account" data-testid="tab-account">{t("settings.account")}</TabsTrigger>
        </TabsList>

        <TabsContent value="profile">
          <div className="rounded-2xl border bg-card p-6 space-y-4">
            <div><Label>{t("auth.fullName")}</Label><Input value={profile.full_name} onChange={(e) => setProfile({ ...profile, full_name: e.target.value })} data-testid="profile-name" /></div>
            <div><Label>{t("settings.professionalTitle")}</Label><Input value={profile.professional_title} onChange={(e) => setProfile({ ...profile, professional_title: e.target.value })} data-testid="profile-title" /></div>
            <div><Label>{t("settings.bio")}</Label><Textarea value={profile.bio} onChange={(e) => setProfile({ ...profile, bio: e.target.value })} data-testid="profile-bio" /></div>
            <div><Label>{t("settings.phone")}</Label><Input value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} data-testid="profile-phone" /></div>
            <Button className="rounded-full" onClick={saveProfile} disabled={loading} data-testid="save-profile-btn">{t("common.save")}</Button>
          </div>
        </TabsContent>

        <TabsContent value="preferences">
          <div className="rounded-2xl border bg-card p-6 space-y-4">
            <div><Label>{t("settings.language")}</Label>
              <Select value={lang} onValueChange={(v) => { setLang(v); saveSettings({ language: v }); }}>
                <SelectTrigger data-testid="settings-language"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="fr">Français</SelectItem><SelectItem value="en">English</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>{t("settings.country")}</Label>
              <Select value={user?.settings?.country_code || ""} onValueChange={(v) => saveSettings({ country_code: v })}>
                <SelectTrigger data-testid="settings-country"><SelectValue /></SelectTrigger>
                <SelectContent>{countries.map((c) => <SelectItem key={c.code} value={c.code}>{lang === "fr" ? c.name_fr : c.name_en}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>{t("settings.currency")}</Label>
              <Select value={user?.settings?.currency || ""} onValueChange={(v) => saveSettings({ currency: v })}>
                <SelectTrigger data-testid="settings-currency"><SelectValue /></SelectTrigger>
                <SelectContent>{["CAD", "USD", "EUR", "XOF", "GBP"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="account">
          <div className="space-y-4">
            <div className="rounded-2xl border bg-card p-6">
              <div className="flex items-center gap-2 text-sm">
                {user?.is_email_verified ? <><CheckCircle2 className="h-5 w-5 text-green-600" /><span>{t("settings.emailVerified")}</span></> : <><AlertCircle className="h-5 w-5 text-amber-500" /><span>{t("settings.emailNotVerified")}</span></>}
                <span className="text-muted-foreground ml-auto">{user?.email}</span>
              </div>
            </div>
            <div className="rounded-2xl border bg-card p-6 space-y-4">
              <h3 className="font-display font-semibold">{t("settings.changePassword")}</h3>
              <div><Label>{t("settings.currentPassword")}</Label><Input type="password" value={pwd.current_password} onChange={(e) => setPwd({ ...pwd, current_password: e.target.value })} data-testid="current-password" /></div>
              <div><Label>{t("settings.newPassword")}</Label><Input type="password" value={pwd.new_password} onChange={(e) => setPwd({ ...pwd, new_password: e.target.value })} data-testid="new-password" /></div>
              <Button className="rounded-full" onClick={changePwd} data-testid="change-password-btn">{t("common.save")}</Button>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
